import React from "react";

import {
  Building2,
  Home,
  LogIn,
  LogOut,
  ShieldCheck,
  Wrench,
} from "lucide-react";

import { useTranslation } from "react-i18next";

import { useAuth0 } from "@auth0/auth0-react";

import "./LoginButton.css";

const AUTH_CONNECTION_KEY = "legup_auth_connection";

const LOGIN_OPTIONS = [
  {
    key: "HOMEOWNER",
    connection: "LEGUP-Homebuyers",
    icon: Home,
  },
  {
    key: "LENDER",
    connection: "LEGUP-Lenders",
    icon: Building2,
  },
  {
    key: "OSS",
    connection: "LEGUP-OSS",
    icon: Wrench,
  },
  {
    key: "ADMIN",
    connection: "Username-Password-Authentication",
    icon: ShieldCheck,
  },
];

export default function LoginButton() {
  const { t } = useTranslation("home");

  const { loginWithRedirect, logout, isAuthenticated, isLoading } = useAuth0();

  const [open, setOpen] = React.useState(false);

  const [redirectingConnection, setRedirectingConnection] = React.useState("");

  const closeTimerRef = React.useRef(null);

  /* =========================================================
     DROPDOWN
     ========================================================= */

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);

      closeTimerRef.current = null;
    }
  };

  const openDropdown = () => {
    if (isAuthenticated) {
      return;
    }

    clearCloseTimer();

    setOpen(true);
  };

  const scheduleCloseDropdown = () => {
    if (isAuthenticated) {
      return;
    }

    clearCloseTimer();

    closeTimerRef.current = window.setTimeout(() => {
      setOpen(false);
    }, 160);
  };

  React.useEffect(() => {
    return () => {
      clearCloseTimer();
    };
  }, []);

  React.useEffect(() => {
    if (isAuthenticated) {
      setOpen(false);
    }
  }, [isAuthenticated]);

  React.useEffect(() => {
    const handlePageShow = () => {
      if (!isAuthenticated) {
        setRedirectingConnection("");
      }
    };

    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [isAuthenticated]);

  /* =========================================================
     LOGIN
     ========================================================= */

  const handleLogin = async (connection) => {
    if (isLoading || isAuthenticated || redirectingConnection || !connection) {
      return;
    }

    try {
      setRedirectingConnection(connection);

      sessionStorage.setItem(AUTH_CONNECTION_KEY, connection);

      await loginWithRedirect({
        appState: {
          returnTo: "/post-login",
        },

        authorizationParams: {
          prompt: "login",
          screen_hint: "login",
          connection,
        },
      });
    } catch (error) {
      console.error("Login redirect failed:", error);

      setRedirectingConnection("");
    }
  };

  /* =========================================================
     LOGOUT
     ========================================================= */

  const handleLogout = () => {
    if (isLoading || !isAuthenticated) {
      return;
    }

    setOpen(false);

    sessionStorage.removeItem(AUTH_CONNECTION_KEY);

    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  /* =========================================================
     MAIN BUTTON
     ========================================================= */

  const handleMainButtonClick = () => {
    if (isAuthenticated) {
      handleLogout();

      return;
    }

    setOpen((previous) => !previous);
  };

  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <div
      className="login-dropdown"
      onMouseEnter={openDropdown}
      onMouseLeave={scheduleCloseDropdown}
      onFocus={openDropdown}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        className={[
          "login-button",
          open && !isAuthenticated ? "login-button--open" : "",
          isAuthenticated ? "login-button--logout" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={handleMainButtonClick}
        disabled={isLoading}
        aria-label={
          isAuthenticated
            ? t("logout.button", "Logout")
            : t("login.button", "Login")
        }
        aria-haspopup={isAuthenticated ? undefined : "menu"}
        aria-expanded={isAuthenticated ? undefined : open}
      >
        <span className="login-button__text">
          {isLoading
            ? t("login.loading", "Loading...")
            : isAuthenticated
              ? t("logout.button", "Logout")
              : t("login.button", "Login")}
        </span>

        <span className="login-button__icon">
          {isAuthenticated ? <LogOut size={19} /> : <LogIn size={19} />}
        </span>
      </button>

      {!isAuthenticated && (
        <div
          className={[
            "login-dropdown__menu",
            open ? "login-dropdown__menu--open" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          role="menu"
          aria-hidden={!open}
        >
          <div className="login-dropdown__header">
            <span className="login-dropdown__eyebrow">
              {t("login.dropdown.eyebrow", "ACCOUNT TYPE")}
            </span>

            <span className="login-dropdown__title">
              {t("login.dropdown.title", "Log in as")}
            </span>
          </div>

          <div className="login-dropdown__options">
            {LOGIN_OPTIONS.map(({ key, connection, icon: Icon }) => {
              const redirecting = redirectingConnection === connection;

              return (
                <button
                  key={key}
                  type="button"
                  className={[
                    "login-dropdown__option",
                    key === "ADMIN" ? "login-dropdown__option--admin" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  role="menuitem"
                  disabled={isLoading || Boolean(redirectingConnection)}
                  onClick={() => handleLogin(connection)}
                >
                  <span className="login-dropdown__option-icon">
                    <Icon size={18} />
                  </span>

                  <span className="login-dropdown__option-content">
                    <strong>{t(`login.roles.${key}.label`)}</strong>
                  </span>

                  <span className="login-dropdown__option-arrow">
                    {redirecting ? "..." : "→"}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
